// src/portal-app/shell/kit/icons.js
//
// Shared inline SVG icon set for the portal-app kit. Inline SVG only —
// never emoji (see spec §4). Every icon is aria-hidden; the *control*
// wrapping it carries the accessible name.
import React from 'react';

const base = (size) => ({
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
});

export const IconChevron = ({ size = 16 }) => (
    <svg {...base(size)}><path d="M9 18l6-6-6-6" /></svg>
);
export const IconBack = ({ size = 20 }) => (
    <svg {...base(size)}><path d="M15 18l-6-6 6-6" /></svg>
);
export const IconFilter = ({ size = 16 }) => (
    <svg {...base(size)}>
        <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" />
        <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
);
export const IconPlus = ({ size = 16 }) => (
    <svg {...base(size)}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);
export const IconAlert = ({ size = 16 }) => (
    <svg {...base(size)}>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
export const IconSearch = ({ size = 16 }) => (
    <svg {...base(size)}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
);
export const IconClose = ({ size = 16 }) => (
    <svg {...base(size)}><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>
);
export const IconArrowUp = ({ size = 12 }) => (
    <svg {...base(size)}><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>
);
export const IconArrowDown = ({ size = 12 }) => (
    <svg {...base(size)}><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>
);
export const IconMore = ({ size = 20 }) => (
    <svg {...base(size)}>
        <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
);
export const IconPhone = ({ size = 16 }) => (
    <svg {...base(size)}>
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
    </svg>
);
export const IconMessage = ({ size = 16 }) => (
    <svg {...base(size)}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
);
export const IconEdit = ({ size = 16 }) => (
    <svg {...base(size)}>
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4z" />
    </svg>
);
export const IconCash = ({ size = 16 }) => (
    <svg {...base(size)}>
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
export const IconCard = ({ size = 16 }) => (
    <svg {...base(size)}>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
);
// Price-tag glyph — CondensedRow's icon slot renders as a blank tinted
// square when no icon is passed (see TransactionsScreen.js file header for
// the defect this repeats). No existing icon here represented "product", so
// this one was added for ProductsScreen (Plan 4 Task 4) and is reusable by
// later P4 catalogue-ish screens (e.g. Inventory).
export const IconTag = ({ size = 16 }) => (
    <svg {...base(size)}>
        <path d="M20.59 13.41L11 3.83A2 2 0 009.59 3.24L4 3a1 1 0 00-1 1l.24 5.59a2 2 0 00.59 1.41l9.58 9.58a2 2 0 002.83 0l4.35-4.35a2 2 0 000-2.83z" />
        <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
);
// Package/box glyph — CondensedRow's icon slot renders as a blank tinted
// square when no icon is passed (see TransactionsScreen.js file header for
// the defect this repeats). No existing icon here represented "stock/
// ingredient", so this one was added for InventoryScreen (Plan 4 Task 5)
// and is reusable by later P4 stock-related screens.
export const IconPackage = ({ size = 16 }) => (
    <svg {...base(size)}>
        <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
        <path d="M3 8l9 5 9-5" />
        <path d="M12 13v8" />
    </svg>
);
// Device/tablet glyph — same shape the pre-migration DevicesScreen used
// inline for its empty state, and the one frontend/src/pages/portal/
// PortalDevicesPage.js's SmartphoneIcon already draws, so this keeps a
// consistent glyph for "device" across web and app. Added for DevicesScreen
// (Plan 4 Task 11) — no existing icon here represented "device".
export const IconDevice = ({ size = 16 }) => (
    <svg {...base(size)}>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
);
